import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { TranslationLanguageMultiSelector } from './TranslationLanguageMultiSelector'

interface Language {
  code: string
  name: string
  flag: string
}

interface AITranslationModalProps {
  availableLanguages: readonly Language[]
  existingTranslations: string[]
  onTranslate: (sourceLanguage: string, targetLanguages: string[], maxConcurrency?: number) => void
  onClose: () => void
}

export function AITranslationModal({ 
  availableLanguages, 
  existingTranslations, 
  onTranslate, 
  onClose 
}: AITranslationModalProps) {
  const [sourceLanguage, setSourceLanguage] = useState('')
  const [targetLanguages, setTargetLanguages] = useState<string[]>([])
  
  // Default DeepSeek configuration values (hidden from admin UI)
  // const useDeepSeekConfig = true
  // const temperature = 1.3
  // const useJsonMode = true
  const maxConcurrency = 20
  
  // Use dynamic languages as fallback if availableLanguages is empty
  const { languages: dynamicLanguages } = useLanguages()
  const fallbackLanguages = convertToTranslationFormat(dynamicLanguages)
  const languagesToUse = availableLanguages.length > 0 ? availableLanguages : fallbackLanguages

  const availableSourceLanguages = languagesToUse.filter(lang => 
    existingTranslations.includes(lang.code)
  )

  const availableTargetLanguages = languagesToUse.filter(lang => 
    lang.code !== sourceLanguage
  )

  const handleTargetLanguageToggle = (languageCode: string) => {
    setTargetLanguages(prev => 
      prev.includes(languageCode)
        ? prev.filter(code => code !== languageCode)
        : [...prev, languageCode]
    )
  }

  const handleTranslate = () => {
    if (!sourceLanguage) {
      toast.error('Please select a source language')
      return
    }

    if (targetLanguages.length === 0) {
      toast.error('Please select at least one target language')
      return
    }

    const sourceLang = languagesToUse.find(lang => lang.code === sourceLanguage)
    const targetLangs = targetLanguages.map(code => 
      languagesToUse.find(lang => lang.code === code)?.name || code
    )

    toast.success(
      `Starting parallel AI translation from ${sourceLang?.name} to ${targetLangs.join(', ')}`
    )

    onTranslate(sourceLanguage, targetLanguages, maxConcurrency)
  }

  const selectAllTargets = () => {
    setTargetLanguages(availableTargetLanguages.map(lang => lang.code))
  }

  const clearAllTargets = () => {
    setTargetLanguages([])
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🤖</span>
              AI Translation Setup
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">

          {/* Source Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Source Language (with existing translation)
            </label>
            
            {availableSourceLanguages.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                No languages with complete translations found. Please add at least name and description in one language first.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {availableSourceLanguages.map((language) => (
                  <label key={language.code} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="sourceLanguage"
                      value={language.code}
                      checked={sourceLanguage === language.code}
                      onChange={(e) => setSourceLanguage(e.target.value)}
                      className="mr-3"
                    />
                    <span className="mr-2">{language.flag}</span>
                    <span className="text-sm font-medium">{language.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Target Languages */}
          {sourceLanguage && (
            availableTargetLanguages.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                No target languages available.
              </div>
            ) : (
              <TranslationLanguageMultiSelector
                label="Target Languages (✓ = has existing translation)"
                selectedLanguages={targetLanguages}
                onToggleLanguage={handleTargetLanguageToggle}
                onSelectAll={selectAllTargets}
                onClearAll={clearAllTargets}
                languages={availableTargetLanguages}
                selectAllText="Select All"
                clearAllText="Clear All"
                existingTranslations={existingTranslations}
              />
            )
          )}

        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleTranslate}
              disabled={!sourceLanguage || targetLanguages.length === 0}
              className="px-4 py-2 bg-gray-600 text-white rounded-full hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center"
            >
              <span className="mr-2">🚀</span>
              Start Parallel Translation
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 